import { handleErrorResponse } from '@frontend/api-client/utils';
import { match } from '@frontend/duck-tape';
import { accountCreationFormDefaultValues, useAccountCreationForm } from '@frontend/forms';
import { signUpRouteValidator } from '@frontend/member-route-validators';
import { useEffect, useFeatureIsOn, useMemo, useState, type ReactNode } from '@frontend/react';
import {
  AppShell,
  Banner,
  Button,
  ClerkLoading,
  ClerkUnavailable,
  LoadingSpinnerPanel,
  PasswordInput,
  ScrollableContentPanel,
  TextInput,
  YStack,
  useSSOLoadState,
} from '@frontend/web-react';
import {
  AppContentWrapper,
  FRICTIONLESS_STEPS,
  OnboardingCard,
  OnboardingPageWrapper,
  SignUpLanding,
  UnauthenticatedAppHeader,
} from '@frontend/web/components';
import { GenericErrorPage } from '@frontend/web/errorPages';
import {
  useAnalytics,
  useAuthentication,
  useLazyRetrieveInviteQuery,
  useLazyRetrievePromotionDetailsQuery,
} from '@frontend/web/hooks';
import { Navigate } from '@tanstack/react-router';
import { useSignUpFlow } from '../hooks/useSignUpFlow';
import {
  AUTHENTICATED_HOME_ROUTE,
  createFileRoute,
  getInviteErrorStateFromError,
  handleMutation,
  redirect,
} from '../utils';

const SignUpEmailContent = () => {
  const ssoLoadState = useSSOLoadState();
  const navigate = Route.useNavigate();
  const searchParams = Route.useSearch();
  const { getURLParamData, setURLParamData } = useAuthentication();
  const { track } = useAnalytics();
  const [emailError, setEmailError] = useState<string | null>(null);
  const storedUrlParamData = getURLParamData();
  const inviteId = searchParams.inviteId ?? storedUrlParamData?.inviteId;
  const promo = searchParams.promo ?? storedUrlParamData?.promo;
  const searchParamsForSignup = {
    ...searchParams,
    ...(promo ? { promo } : {}),
  };
  const isFrictionlessFlow = searchParams.utm_campaign === 'try-first';

  const isAcceptInviteFlow = Boolean(inviteId);

  useEffect(() => {
    setURLParamData({
      ...(inviteId ? { inviteId } : {}),
      ...(promo ? { promo } : {}),
      ...(searchParams.referrer ? { referrer: searchParams.referrer } : {}),
    });
  }, [inviteId, promo, searchParams.referrer, setURLParamData]);

  useEffect(() => {
    track({
      data: {
        entryType: isAcceptInviteFlow ? 'accept-invite' : 'signup',
        flowVariant: 'v2',
        platform: 'web',
        step: 'email-form',
      },
      event: 'signupStepViewed',
      topic: 'auth',
    });
  }, [isAcceptInviteFlow, track]);

  const [retrieveInvite, { data: inviteData, error: retrieveInviteError, isLoading: isInviteLoading }] =
    useLazyRetrieveInviteQuery();
  const [retrievePromotionDetails] = useLazyRetrievePromotionDetailsQuery();
  const inviteErrorState = useMemo(() => getInviteErrorStateFromError(retrieveInviteError), [retrieveInviteError]);

  useEffect(() => {
    if (inviteId) {
      retrieveInvite(inviteId);
    }
  }, [inviteId, retrieveInvite]);

  const {
    createClientError,
    handleSubmitForm,
    isAnyLoading: isSignUpLoading,
  } = useSignUpFlow({
    inviteData,
    navigateOnSuccess: navigate,
    searchParams: searchParamsForSignup,
    type: isAcceptInviteFlow ? 'accept-invite' : 'signup',
  });

  const isAnyLoading = isInviteLoading || isSignUpLoading;
  const hasInviteError = Boolean(retrieveInviteError);
  const shouldRenderInviteErrorInline = hasInviteError && inviteErrorState.renderMode === 'inline';
  const inviteErrorMessage = inviteErrorState.description || 'We could not load your invite. Please try again.';

  useEffect(() => {
    if (createClientError) {
      handleErrorResponse(createClientError, (error) => {
        const errorData = error.data as { errors?: Array<{ field: string; message: string }> } | undefined;
        if (errorData?.errors?.some((e) => e.field === 'email')) {
          setEmailError('An account with this email already exists. Please sign in instead.');
        }
      });
    }
  }, [createClientError]);

  const { dirty, getControl, handleSubmit, isValid, setFieldValue } = useAccountCreationForm({
    initialValues: accountCreationFormDefaultValues,
    onSubmit: handleSubmitForm,
  });

  useEffect(() => {
    if (!promo) {
      setFieldValue('promoCode', '');
      return;
    }

    handleMutation({
      mutation: () => retrievePromotionDetails(promo).unwrap(),
      onError: () => {
        setFieldValue('promoCode', '');
      },
      onSuccess: () => {
        setFieldValue('promoCode', promo);
      },
      showErrorToast: false,
      showSuccessToast: false,
    });
  }, [promo, retrievePromotionDetails, setFieldValue]);

  useEffect(() => {
    if (inviteData) {
      setFieldValue('email', inviteData.email || '');
      setFieldValue('firstName', inviteData.firstName || '');
      setFieldValue('lastName', inviteData.lastName || '');
      setFieldValue('nickname', inviteData.nickname || '');
      setFieldValue('phoneNumber', inviteData.phoneNumber);
    }
  }, [inviteData, setFieldValue]);

  const isEmailDisabled = !!inviteData?.email;

  const showLoading = isAcceptInviteFlow && isInviteLoading;
  const showInviteError = isAcceptInviteFlow && hasInviteError;
  const canShowForm = !showLoading && !showInviteError && (!isAcceptInviteFlow || inviteData);

  const renderContent = () => {
    if (showLoading) {
      return <LoadingSpinnerPanel fullScreen />;
    }

    if (showInviteError && shouldRenderInviteErrorInline) {
      return (
        <SignUpLanding
          disableSignUpActions
          inviteErrorMessage={inviteErrorMessage}
          searchParams={searchParamsForSignup}
        />
      );
    }

    if (showInviteError) {
      return <GenericErrorPage giantText="Oops," subtitle={inviteErrorMessage} title="" />;
    }

    if (!canShowForm) {
      return null;
    }

    return (
      <OnboardingPageWrapper currentStep="createAccount" steps={isFrictionlessFlow ? FRICTIONLESS_STEPS : undefined}>
        <OnboardingCard title={"Let's create your account"}>
          {emailError ? <Banner color="danger" leftIconName="IconAlertCircle" title={emailError} /> : null}

          <form className="w-full" onSubmit={handleSubmit}>
            <YStack className="w-full" gapY="lg">
              {isAcceptInviteFlow && getControl('phoneNumber').value ? (
                <TextInput
                  {...getControl('phoneNumber')}
                  accessibilityLabel="Phone number"
                  className="hidden"
                  disabled
                  readOnly
                  value={getControl('phoneNumber').value || ''}
                />
              ) : null}

              <TextInput
                {...getControl('firstName')}
                accessibilityLabel="First name"
                autoComplete="given-name"
                label="First name"
                placeholder="First name"
              />
              <TextInput
                {...getControl('lastName')}
                accessibilityLabel="Last name"
                autoComplete="family-name"
                label="Last name"
                placeholder="Last name"
              />
              <YStack gapY="xs">
                <TextInput
                  {...getControl('email')}
                  accessibilityLabel="Email address"
                  autoComplete="email"
                  disabled={isEmailDisabled}
                  label="Email address"
                  placeholder="The email you actually check"
                  type="email"
                />
                {isEmailDisabled ? (
                  <a
                    className="text-neutral-500 underline hover:text-neutral-700"
                    href="mailto:support@duckbill.com?subject=Wrong email on invite"
                  >
                    Wrong email? Contact us
                  </a>
                ) : null}
              </YStack>
              <PasswordInput
                {...getControl('password')}
                accessibilityLabel="Password"
                label="Password"
                placeholder="At least 8 characters + 1 number"
              />
              <PasswordInput
                {...getControl('confirmPassword')}
                accessibilityLabel="Confirm your password"
                label="Confirm your password"
                placeholder="Match the password above"
              />

              <Button
                accessibilityLabel="Next"
                disabled={!isValid || !dirty || isAnyLoading}
                fullWidth
                isForm
                isLoading={isAnyLoading}
                label="Next"
                variant={isValid && dirty ? 'primary' : 'secondary'}
              />
            </YStack>
          </form>
        </OnboardingCard>
      </OnboardingPageWrapper>
    );
  };

  const renderFallback = (inner: ReactNode) => (
    <ScrollableContentPanel containerClassName="bg-cream-100" contentWidth="max-w-xl w-full" justifyContent="center">
      <OnboardingCard>{inner}</OnboardingCard>
    </ScrollableContentPanel>
  );

  const panelContent = match(ssoLoadState)
    .with({ status: 'loading' }, () => renderFallback(<ClerkLoading />))
    .with({ status: 'failure' }, ({ onReload }) => renderFallback(<ClerkUnavailable onAction={onReload} />))
    .with({ status: 'success' }, () => renderContent())
    .exhaustive();

  return (
    <AppShell>
      <UnauthenticatedAppHeader linkTo={false} />
      <AppContentWrapper className="flex-1">{panelContent}</AppContentWrapper>
    </AppShell>
  );
};

/**
 * Email registration route for the new signup flow.
 *
 * Feature Flag Behavior (is_signup_changes_enabled):
 * - Flag ON: Renders email registration form (new flow)
 * - Flag OFF: Redirects to /sign-up (this route doesn't exist in old flow)
 */
const SignUpEmail = () => {
  const { enabled: isSignupChangesEnabled } = useFeatureIsOn('is_signup_changes_enabled');

  // When flag is OFF, redirect to /sign-up since this route doesn't exist in the old flow
  if (!isSignupChangesEnabled) {
    return <Navigate to="/sign-up" />;
  }

  return <SignUpEmailContent />;
};

export const Route = createFileRoute('/sign-up/email')({
  beforeLoad: async ({ context }) => {
    if (context.authentication?.isAuthenticated) {
      throw redirect({ to: AUTHENTICATED_HOME_ROUTE });
    }
  },
  component: SignUpEmail,
  validateSearch: signUpRouteValidator,
});
